'use client';

import Link from 'next/link';
import { useMemo, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { projects } from '@/data/projects';
import type { Project } from '@/types/project';

interface ProjectSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProjectSearchModal({
  open,
  onOpenChange,
}: ProjectSearchModalProps) {
  const [query, setQuery] = useState('');

  const filteredProjects = useMemo(() => {
    if (!query) return projects;
    const lowerQuery = query.toLowerCase();
    return projects.filter(
      (project) =>
        project.title.toLowerCase().includes(lowerQuery) ||
        project.description.toLowerCase().includes(lowerQuery) ||
        project.technologies.some((tech) =>
          tech.toLowerCase().includes(lowerQuery)
        )
    );
  }, [query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="m-2 mx-auto w-full p-2 md:p-4">
        <DialogHeader>
          <DialogTitle>Search Projects</DialogTitle>
          <DialogDescription>Type to find a project</DialogDescription>
        </DialogHeader>
        <Input
          placeholder="Search projects..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoFocus
          className="mb-4"
        />
        <ul className="max-h-80 space-y-2 overflow-y-auto">
          {filteredProjects.map((project: Project) => (
            <li key={project.id}>
              <Link
                href={`/projects/${project.id}`}
                className="text-foreground hover:bg-accent hover:text-accent-foreground focus:ring-accent focus:ring-offset-background focus-visible:ring-ring active:bg-accent/80 aria-current:page:bg-accent aria-current:page:text-accent-foreground block rounded-md p-2 font-medium transition-colors duration-150 ease-in-out focus:ring-2 focus:ring-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:outline-none"
              >
                <span className="font-semibold">{project.title}</span>
                <p className="text-muted-foreground text-sm">
                  {project.description}
                </p>
              </Link>
            </li>
          ))}
          {filteredProjects.length === 0 && (
            <p className="text-muted-foreground">No projects found.</p>
          )}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
